import { Eye, EyeOff } from "lucide-react";
import { useMemo } from "react";
import { type MediaItem, SortableMediaList } from "./SortableMediaList";

type RecordingWithMetadata = {
  id: string;
  filename: string;
  signedAudioUrl: string;
  durationSeconds: number;
  createdAt: string;
  unlisted?: boolean;
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type Props = {
  recordings: RecordingWithMetadata[];
  recordingIds: string[];
  songId: string;
  songTitle: string;
  authorName: string | null;
  isEditing: boolean;
  disabled?: boolean;
  onReorder?: (newOrder: string[]) => void;
  onDelete?: (id: string) => void;
  /** Toggle a recording's visibility (applies immediately, not on save) */
  onToggleVisibility?: (id: string, unlisted: boolean) => void;
  emptyState?: React.ReactNode;
};

/**
 * Displays a list of recordings with drag-and-drop reordering.
 * Thin wrapper around SortableMediaList that handles the recording-to-MediaItem transformation.
 */
export function RecordingsList({
  recordings,
  recordingIds,
  songId,
  songTitle,
  authorName,
  isEditing,
  disabled,
  onReorder,
  onDelete,
  onToggleVisibility,
  emptyState,
}: Props) {
  const items: MediaItem[] = useMemo(() => {
    const recordingMap = new Map(recordings.map((r) => [r.id, r]));
    const result: MediaItem[] = [];
    for (const [index, id] of recordingIds.entries()) {
      const recording = recordingMap.get(id);
      if (!recording) continue;
      result.push({
        id: recording.id,
        title: recording.filename,
        subtitle: formatDate(recording.createdAt),
        isPrimary: index === 0,
        unlisted: recording.unlisted,
        titleType: "filename",
        audio: {
          recordingId: recording.id,
          filename: recording.filename,
          signedUrl: recording.signedAudioUrl,
          songId,
          songTitle,
          authorName,
          durationSeconds: recording.durationSeconds,
        },
      });
    }
    return result;
  }, [recordings, recordingIds, songId, songTitle, authorName]);

  const renderVisibilityToggle = (item: MediaItem) => {
    if (!onToggleVisibility) return null;
    return (
      <button
        type="button"
        onClick={() => onToggleVisibility(item.id, !item.unlisted)}
        disabled={disabled}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary disabled:opacity-30"
        aria-label={
          item.unlisted ? `Make ${item.title} listed` : `Unlist ${item.title}`
        }
        aria-pressed={item.unlisted}
        title={
          item.unlisted
            ? "Unlisted: hidden from visitors who aren't signed in"
            : "Listed: visible to everyone"
        }
      >
        {item.unlisted ? (
          <EyeOff className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Eye className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
    );
  };

  return (
    <SortableMediaList
      items={items}
      isEditing={isEditing}
      disabled={disabled}
      onReorder={onReorder}
      onDelete={onDelete}
      renderEditControls={renderVisibilityToggle}
      emptyState={emptyState}
      testIdPrefix="recording"
    />
  );
}
